import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Maximize2, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const ImageUpload = ({ images = [], onImagesChange, label, entityType = 'default', viewOnly = false, required = false }) => {
  const [uploading, setUploading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const inputRef = useRef(null);
  const { t } = useTranslation();

  const displayLabel = label || t('components.image_upload.upload_product_image');

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedUrls = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity', entityType);
        const res = await fetch('/api/media/upload', {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data.status === 'success') {
          uploadedUrls.push(data.url);
        }
      }

      const newImages = [...images, ...uploadedUrls];
      onImagesChange(newImages);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
    if (previewIndex !== null) {
      if (index === previewIndex) {
        setPreviewIndex(newImages.length > 0 ? Math.min(index, newImages.length - 1) : null);
      } else if (index < previewIndex) {
        setPreviewIndex(previewIndex - 1);
      }
    }
  };

  // Drag-and-drop reordering
  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newImages = [...images];
    const [moved] = newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, moved);
    onImagesChange(newImages);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-3 w-full">
      {/* Header */}
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{displayLabel}{required && <span className="text-red-500 ml-1">*</span>}</label>
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {images.length > 0 ? t('components.image_upload.image_count', { count: images.length }) : t('components.image_upload.optional')}
        </span>
      </div>

      {/* Lightbox Modal */}
      {previewIndex !== null && images[previewIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center"
          onClick={() => setPreviewIndex(null)}
        >
          <div
            className="relative max-w-5xl max-h-[92vh] mx-4 flex flex-col items-center"
            onClick={e => e.stopPropagation()}
          >
            {/* Counter */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
              {previewIndex + 1} / {images.length}
            </div>

            {/* Close button */}
            <button type="button"
              onClick={() => setPreviewIndex(null)}
              className="absolute -top-10 right-0 p-1.5 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            {/* Image */}
            <img
              src={images[previewIndex]}
              alt=""
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
            />

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <button type="button"
                  onClick={() => setPreviewIndex(prev => prev > 0 ? prev - 1 : images.length - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-sm"
                >
                  <ChevronLeft size={22} />
                </button>
                <button type="button"
                  onClick={() => setPreviewIndex(prev => prev < images.length - 1 ? prev + 1 : 0)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-sm"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}

            {/* Thumbnail strip */}
            <div className="mt-3 flex gap-1.5 max-w-full overflow-x-auto pb-1">
              {images.map((url, i) => (
                <button type="button"
                  key={i}
                  onClick={() => setPreviewIndex(i)}
                  className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    i === previewIndex
                      ? 'border-blue-500 opacity-100'
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-8 gap-3">
        {/* Upload Trigger (hidden in viewOnly) */}
        {!viewOnly && (
          <label className={`
            aspect-square relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all cursor-pointer
            ${uploading
              ? 'bg-slate-100 dark:bg-slate-800 border-blue-400 dark:border-blue-500'
              : 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }
          `}>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {uploading ? (
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-1">
                  <Upload className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">{t('components.image_upload.add_image')}</span>
              </>
            )}
          </label>
        )}

        {/* Preview Images */}
        {images.map((url, index) => (
          <div
            key={index}
            draggable={!viewOnly && !uploading}
            onDragStart={viewOnly ? undefined : () => handleDragStart(index)}
            onDragOver={viewOnly ? undefined : (e) => handleDragOver(e, index)}
            onDragLeave={viewOnly ? undefined : handleDragLeave}
            onDrop={viewOnly ? undefined : (e) => handleDrop(e, index)}
            onDragEnd={viewOnly ? undefined : handleDragEnd}
            onClick={() => setPreviewIndex(index)}
            className={`
              group aspect-square relative rounded-xl overflow-hidden border cursor-pointer shadow-sm transition-all duration-150
              ${!viewOnly && dragIndex === index
                ? 'opacity-50 border-blue-500 border-2 scale-95'
                : !viewOnly && dragOverIndex === index
                  ? 'border-blue-400 border-2'
                  : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md'
              }
              ${!viewOnly && dragIndex !== null && dragIndex !== index && dragOverIndex !== index
                ? 'border-dashed border-slate-300 dark:border-slate-600'
                : ''
              }
            `}
          >
            <img
              src={url}
              alt=""
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.classList.add('bg-slate-100', 'dark:bg-slate-800');
              }}
            />

            {/* Primary image indicator */}
            {index === 0 && (
              <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-yellow-500 text-white text-[10px] rounded font-bold shadow-md flex items-center gap-0.5 z-10">
                <span>★</span>
                <span>{t('components.dynamic_eav_form.image')}</span>
              </div>
            )}

            {/* Hover overlay (hidden in viewOnly — click on image opens preview) */}
            {!viewOnly && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setPreviewIndex(index); }}
                  className="p-1.5 bg-white/90 text-slate-700 rounded-full hover:bg-white transition-colors shadow-lg hover:scale-110 transform"
                  title={t('common.view')}
                >
                  <Maximize2 size={13} />
                </button>
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                  className="p-1.5 bg-red-500/90 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg hover:scale-110 transform"
                  title={t('common.delete')}
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {/* Index badge */}
            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/50 text-white text-[10px] rounded font-mono opacity-0 group-hover:opacity-100 transition-opacity">
              #{index + 1}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state (muted in viewOnly) */}
      {images.length === 0 && !uploading && (
        <div className="text-center py-6 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
          <ImageIcon className={`w-8 h-8 mx-auto mb-2 ${viewOnly ? 'text-slate-200 dark:text-slate-700' : 'text-slate-300 dark:text-slate-600'}`} />
          <p className={`text-xs ${viewOnly ? 'text-slate-300 dark:text-slate-600' : 'text-slate-400 dark:text-slate-500'}`}>
            {viewOnly ? t('components.image_upload.no_images') : t('components.image_upload.drag_drop_image')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
